/* ===========================================================================
 * hc-machinery-shop.js
 * ---------------------------------------------------------------------------
 * Hangcha NZ - card-level purchasing mutators.
 *
 * Companion to `hc-collection-filter-ajax.js`. That file owns the collection
 * view; this one owns what happens when a customer acts on a card:
 *
 *   Tier A  "Available online"  -> POST /cart/add.js, then broadcast the
 *                                  standard cart events so Dawn's cart drawer
 *                                  and cart bubble refresh themselves.
 *   Tier B  "Contact for quote" -> dispatch `quote:request` and open the
 *                                  shared RFQ drawer, pre-filled with the
 *                                  machine reference.
 *
 * Delegated listeners on `document` mean cards injected by the Section
 * Rendering API need no re-binding after a filter change.
 *
 * Events dispatched on `document`
 *   quote:request   { productId, productTitle, productUrl }
 *   cart:refresh    (Dawn convention - no detail)
 *   cart:updated    { cart, source: 'hc-machinery-shop' }
 *   hc:cart:error   { error, variantId }
 * ======================================================================== */

(function () {
  'use strict';

  window.HCTheme = window.HCTheme || {};

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* --- Cart ------------------------------------------------------------- */

  async function addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId || button.getAttribute('aria-busy') === 'true') return;

    const originalLabel = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.disabled = true;

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      const payload = await response.json();

      if (!response.ok) {
        // Shopify returns { description, message } on a 422 (e.g. out of stock).
        throw new Error(payload.description || payload.message || `Cart add failed (${response.status})`);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: payload, source: 'hc-machinery-shop' },
        })
      );

      button.textContent = button.dataset.addedLabel || 'Added';
      setTimeout(() => {
        button.textContent = originalLabel;
      }, 2000);
    } catch (error) {
      console.error('[HCTheme] Add to cart failed:', error);
      button.textContent = error.message;
      document.dispatchEvent(
        new CustomEvent('hc:cart:error', { bubbles: true, detail: { error, variantId } })
      );
      setTimeout(() => {
        button.textContent = originalLabel;
      }, 4000);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  /* --- Quote drawer ------------------------------------------------------ */

  const QuoteDrawer = {
    returnFocus: null,

    get element() {
      return document.querySelector('[data-hc-quote-drawer]');
    },

    open(detail) {
      const drawer = this.element;
      if (!drawer) return;

      this.returnFocus = document.activeElement;
      this.prefill(drawer, detail);

      drawer.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');

      const first = drawer.querySelector('.hc-quote-drawer__panel').querySelector(FOCUSABLE);
      if (first) first.focus();
    },

    close() {
      const drawer = this.element;
      if (!drawer || !drawer.hasAttribute('open')) return;

      drawer.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');

      if (this.returnFocus && document.contains(this.returnFocus)) this.returnFocus.focus();
      this.returnFocus = null;
    },

    prefill(drawer, detail) {
      if (!detail) return;

      const subject = drawer.querySelector('[data-hc-quote-subject]');
      const machine = drawer.querySelector('[data-hc-quote-machine]');
      const machineUrl = drawer.querySelector('[data-hc-quote-machine-url]');
      const message = drawer.querySelector('[data-hc-quote-message]');

      if (subject) subject.textContent = detail.productTitle || '';
      if (machine) machine.value = detail.productTitle || '';
      if (machineUrl) machineUrl.value = detail.productUrl || '';
      if (message && message.value.trim() === '' && detail.productTitle) {
        message.value = `I would like a quote for the ${detail.productTitle}.\n\nApplication:\nLocation:\nRequired by:\n`;
      }
    },
  };

  window.HCTheme.quote = QuoteDrawer;

  /* --- Delegated listeners ------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest('[data-hc-add-to-cart]');
    if (addButton) {
      event.preventDefault();
      addToCart(addButton);
      return;
    }

    const quoteTrigger = event.target.closest('[data-hc-quote-trigger]');
    if (quoteTrigger) {
      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent('quote:request', {
          bubbles: true,
          detail: {
            productId: quoteTrigger.dataset.productId,
            productTitle: quoteTrigger.dataset.productTitle,
            productUrl: quoteTrigger.dataset.productUrl,
          },
        })
      );
      return;
    }

    if (event.target.closest('[data-hc-quote-close]')) {
      event.preventDefault();
      QuoteDrawer.close();
    }
  });

  document.addEventListener('quote:request', (event) => QuoteDrawer.open(event.detail));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') QuoteDrawer.close();
  });
})();
